import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {HideableValue, PartiallyOptional, PartiallyRequired} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    hideableValueCreateHidden,
    hideableValueGetVisibleValue,
    hideableValueIsVisible,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce
} from '@wix/devzai-utils-common';
import type {WixImageResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    BymoPagePropsContentSettingSemanticMeaning, BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    BymoPagePropsSpecialContentSettingMetadata,
    bymoPagePropsCreateContentSettingMetadata,
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    DoppeActionLayout, doppeListActionContentSettingsMetadataCreateForListActionCommonSettings,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemSettingsCreateDefault,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithItemClickBehaviourMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionPartialSettings,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage
} from '../../client-server-common';
import type {DoppeActionTypeContactCard} from '../doppe-action-type-contact-card/doppe-action-type-contact-card';
import {
    doppeActionTypeContactCardActionContentSettingsMetadata
} from '../doppe-action-type-contact-card/doppe-action-type-contact-card';
import type {DoppeActionFullName} from '../../client-server-common/types/doppe-action-full-name';
import {doppeActionFullNameGetName} from '../../client-server-common/types/doppe-action-full-name';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeActionCTAType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {
    DoppeDtoContactMethod,
    DoppeDtoContactMethodDeprecated
} from '../../client-server-common/types/doppe-dto-contact-method';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';


import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';


export namespace DoppeActionTypeContactCardList {

    export interface ContactCardSettings extends
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeActionTypeContactCard.ContactSettings,
        DoppeListActionSettings.ListItemWithItemClickBehaviour {
        name: DoppeActionFullName;
    }

    export interface ActionSettings extends
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings<ContactCardSettings>,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        previewShowTitle: boolean;
        previewShowDescription: boolean;
    }


    export interface DeprecatedFields extends Partial<DoppeListActionSettings.WithCtaButton> {
        contacts?: ContactCardSettings[];
        previewSaveButtonText?: string;
    }

    export interface DeprecatedItemFields extends Partial<DoppeActionCtaButtonSettings> {
        contactMethods?: PartiallyRequired<Partial<DoppeDtoContactMethod & DoppeDtoContactMethodDeprecated>, 'id'>[]
        buttonText?: HideableValue<string>;
        /** @deprecated Migrated to CTA button settings. */
        notes?: string;
        /** @deprecated Migrated to CTA button settings. */
        saveContactProfileImage?: HideableValue<WixImageResource | null>;
    }

    export type PartialProps = DoppeListActionPartialSettings<ActionSettings, ContactCardSettings, DeprecatedFields, DeprecatedItemFields>;
}


export const doppeActionTypeContactCardList = doppeActionTypeDefineMetadata<DoppeActionTypeContactCardList.ActionSettings, DoppeActionTypeContactCardList.PartialProps>({
    id: 'io.bymo.action/contact-card-list',
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            ctaButton,
            previewShowDescription = true,
            previewShowTitle = true,
            itemShowTextOnImage = true,
            layout = DoppeActionLayout.Grid,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            previewShowDescription: previewShowDescription,
            previewShowTitle: previewShowTitle,
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeContactCardList.ContactCardSettings>({
                ...action.settings,
                itemShowTextOnImage: itemShowTextOnImage,
                layout: layout,
                listDataItems: listDataItems.map(contact => {

                    const {
                        buttonText,
                        notes = '',
                        saveContactProfileImage = hideableValueCreateHidden(null),
                        ctaButton,
                        name = {firstName: '', lastName: ''},
                        media = null,
                        description = hideableValueCreateHidden(''),
                        jobTitle = hideableValueCreateHidden(''),
                        company = hideableValueCreateHidden(''),
                        contactMethods = [],
                        ...restContactSettings
                    } = contact;

                    return {
                        ...restContactSettings,
                        ...doppeListActionListItemSettingsMigrateBaseSettings(contact),
                        name: name,
                        media: media,
                        description: description,
                        jobTitle: jobTitle,
                        company: company,
                        ...getDefaultDoppeActionCtaButtonSettings({
                            buttonText: hideableValueGetVisibleValue(buttonText, ''),
                            showButton: hideableValueIsVisible(buttonText),
                            settings: {
                                type: DoppeActionCTAType.SaveContact,
                                notes: notes,
                                saveContactProfileImage: saveContactProfileImage
                            }
                        }),
                        ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                            hasSourceURL: false,
                            ctaButton: ctaButton,
                        }),
                        contactMethods: contactMethods.map(method => {

                            const {
                                hidden = false
                            } = (method as DoppeActionTypeContactCard.xxx)

                            return {
                                ...doppeListActionListItemSettingsCreateDefault(method.id),
                                enabled: !hidden,
                                ...method as PartiallyOptional<DoppeDtoContactMethod, 'enabled'>,
                            } satisfies DoppeDtoContactMethod;
                        }),
                        ...doppeListActionListItemWithCtaButtonsMigration(contact, false),
                        ...doppeListActionListItemWithItemClickBehaviourMigration(contact),
                        ...doppeListActionListItemWithSectionsMigration(contact),
                    };
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({}, action.settings),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                ctaButton: ctaButton,
                hasSourceURL: false
            }),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeContactCardList.ActionSettings;
    },
    canRenderInPage: true,
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
            ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings(),
            previewShowTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowTitle'}),
            previewShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowDescription'}),
            allowItemClickBehaviourPerItem: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            },
        bindableContentSettingsNames: {}
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeActionTypeContactCardActionContentSettingsMetadata,
            name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.PersonFullName, BymoPagePropsContentSettingSemanticMeaning.PersonName)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                company: true,
                description: true,
                itemClickBehaviour: true,
                jobTitle: true,
            },
        bindableContentSettingsNames: {}
    },
    isWidgetOpener: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: false,
    supportsListLayout: true,
    resolveListItemsCount: actionSettings => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            return {
                url: doppeActionCtaButtonsResolveMainURL(
                    doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item), {
                        sourceLinkSpec: null,
                        viewerLang: viewerLang,
                        bymoPageProps: bymoPageProps,
                        viewerPagesRoutesManager: viewerPagesRoutesManager,
                        bymoPageContextProps: bymoPageContextProps
                    }) ?? null,
                title: doppeActionFullNameGetName(item.name),
                action: action,
                description: item.description,
                image: item.media?.[0] ?? null,
                itemId: item.id,
                couponCode: hideableValueCreateHidden(''),
                ribbon: hideableValueCreateHidden(''),
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(actionSettings.listDataItems.map(itemSettings => {
            return itemSettings.media ?? []
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, actionSettings.listDataItems[0]?.media?.[0] ?? null);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const contact of actionSettings.listDataItems) {
                if (contact.media) {
                    contact.media = contact.media.map(
                        image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});

